import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, InputGroup, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchArticles, searchContent, fetchLatestContentByType, fetchContentByTypeWithPagination } from "../services/ContentService";
import ContentData from "../models/ContentData";
import { IMAGE_BASE } from "../Config";

const ArticlesPage: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<ContentData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [initialLoad, setInitialLoad] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!initialLoad) {
      loadArticles(true);
    }
  }, [initialLoad]);

  const loadArticles = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);
      const currentPage = reset ? 1 : page;
      const response = await fetchContentByTypeWithPagination('article', currentPage, 12);

      if (reset) {
        setArticles(response.contents);
        setPage(currentPage + 1);
        setInitialLoad(true);
      } else {
        // Filter out duplicates by ID
        const newArticles = response.contents.filter(newArticle => 
          !articles.some(existingArticle => existingArticle._id === newArticle._id)
        );
        setArticles(prev => [...prev, ...newArticles]);
        setPage(currentPage + 1);
      }
      setHasMore(response.hasMore);
    } catch (err) {
      setError("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadArticles(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await searchContent(searchQuery);
      // Filter for articles only
      const articleResults = results.filter(item => 
        item.contentType === "Article" || 
        (typeof item.contentType === 'object' && (item.contentType as any).name?.toLowerCase() === "article")
      );
      setArticles(articleResults);
      setHasMore(false);
    } catch (err) {
      setError("No articles found for your search");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = (id: string) => {
    navigate(`/article/${id}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Articles | Natakiya - Theater Articles & Stories</title>
        <meta name="description" content="Discover inspiring theater articles, stories, and insights from the world of performing arts on Natakiya." />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content="Articles | Natakiya - Theater Articles & Stories" />
        <meta property="og:description" content="Discover inspiring theater articles, stories, and insights from the world of performing arts on Natakiya." />
        <meta property="og:image" content={`${window.location.origin}/logo.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Natakiya" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Articles | Natakiya - Theater Articles & Stories" />
        <meta name="twitter:description" content="Discover inspiring theater articles, stories, and insights from the world of performing arts on Natakiya." />
        <meta name="twitter:image" content={`${window.location.origin}/logo.png`} />
      </Helmet>
      
      <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)' }}
            >
              <i className="bi bi-newspaper text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#810081' }}>Articles</h1>
              <p className="text-muted mb-0">Discover inspiring theater articles</p>
            </div>
          </div>
        </div>

        {/* Modern Search Section */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="justify-content-center">
              <Col lg={8}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search articles by title or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="ps-5 py-3 border-0 bg-light"
                    style={{ borderRadius: '50px', fontSize: '16px' }}
                  />
                  <i 
                    className="bi bi-search position-absolute" 
                    style={{ left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#810081' }}
                  ></i>
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading}
                    className="position-absolute border-0"
                    style={{ 
                      right: '8px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: '#FFD600',
                      color: '#810081',
                      borderRadius: '50px',
                      width: '40px',
                      height: '40px'
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <i className="bi bi-search"></i>}
                  </Button>
                </div>
              </Col>
            </Row>
            
            {/* Active Search Pill */}
            {searchQuery && (
              <div className="mt-3 d-flex justify-content-center">
                <span 
                  className="badge d-flex align-items-center"
                  style={{ backgroundColor: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                >
                  Search: "{searchQuery}"
                  <button 
                    className="btn-close btn-close-white ms-2" 
                    style={{ fontSize: '10px' }}
                    onClick={() => {
                      setSearchQuery("");
                    }}
                  ></button>
                </span>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modern Results Header */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#810081' }}>
                {articles.length > 0 ? `${articles.length} Articles Found` : "No Articles Found"}
              </h5>
              <small className="text-muted">
                {searchQuery && `Searching for "${searchQuery}"`}
              </small>
            </div>
            {/* {articles.length > 0 && (
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="rounded-pill"
                onClick={() => {
                  const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                  setSortOrder(newOrder);
                  const sortedArticles = [...articles].sort((a, b) => {
                    return newOrder === 'asc' 
                      ? a.title.localeCompare(b.title)
                      : b.title.localeCompare(a.title);
                  });
                  setArticles(sortedArticles);
                }}
              >
                <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
                Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )} */}
          </div>
        )}

        {/* Modern Error Message */}
        {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              color: '#810081',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#FFD600' }}></i>
            {error}
          </Alert>
        )}

        {/* Modern Articles Grid */}
        <Row className="g-4">
          {articles.map((article) => (
            <Col key={article._id} lg={6} md={6}>
              <Card 
                className="h-100 border-0" 
                onClick={() => handleArticleClick(article._id)}
                style={{ 
                  cursor: "pointer", 
                  borderRadius: '20px',
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: '0 4px 20px rgba(129, 0, 129, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(129, 0, 129, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(129, 0, 129, 0.1)";
                }}
              >
              <div className="position-relative overflow-hidden" style={{ borderRadius: '20px 20px 0 0' }}>
                {article.imageLink ? (
                  <Card.Img 
                    variant="top" 
                    src={IMAGE_BASE + article.imageLink} 
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                ) : (
                  <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "200px", background: "linear-gradient(135deg, #FFD600 0%, #810081 100%)" }}
                  >
                    <i className="bi bi-newspaper text-white" style={{ fontSize: "2.5rem" }}></i>
                  </div>
                )}
                <div className="position-absolute top-0 start-0 w-100 h-100" 
                     style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)' }}></div>
              </div>
              
              <Card.Body className="d-flex flex-column p-4">
                <Card.Title className="fw-bold mb-2" style={{ color: '#810081', fontSize: '1.1rem' }}>
                  {article.title}
                </Card.Title>
                <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                  {article.content && article.content.length > 150 
                    ? `${article.content.substring(0, 150)}...` 
                    : article.content || "Click to read this interesting article."}
                </Card.Text>
                {/* Writer Pill */}
                {article.writer && (
                  <div className="mb-3">
                    <span 
                      className="badge d-inline-flex align-items-center"
                      style={{ 
                        backgroundColor: 'rgba(129, 0, 129, 0.1)', 
                        color: '#810081', 
                        borderRadius: '50px',
                        padding: '6px 12px',
                        fontSize: '0.8rem'
                      }}
                    >
                      <i className="bi bi-person me-1"></i>
                      {article.writer}
                    </span>
                  </div>
                )}
                
                {/* Action Button */}
                <div className="d-flex justify-content-end mt-auto">
                  <small style={{ color: '#FFD600', fontWeight: '600' }}>
                    Read More <i className="bi bi-arrow-right ms-1"></i>
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

        {/* Modern Load More Button */}
        {hasMore && articles.length > 0 && !searchQuery && (
          <div className="text-center mt-5">
            <Button 
              onClick={() => loadArticles()} 
              disabled={loading}
              className="rounded-pill border-0 px-5 py-3"
              style={{ 
                background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 15px rgba(129, 0, 129, 0.3)'
              }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading More...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Load More Articles
                </>
              )}
            </Button>
          </div>
        )}

        {/* Modern Empty State */}
        {!loading && articles.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, rgba(255, 214, 0, 0.2) 0%, rgba(129, 0, 129, 0.2) 100%)' 
                }}
              >
                <i className="bi bi-newspaper" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#810081' }}>No Articles Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {searchQuery 
                  ? "We couldn't find any articles matching your search. Try adjusting your criteria." 
                  : "Check back soon for new inspiring theater articles!"}
              </p>
              {searchQuery && (
                <Button 
                  onClick={() => {
                    setSearchQuery("");
                    loadArticles(true);
                  }}
                  className="rounded-pill border-0 px-4 py-2"
                  style={{ backgroundColor: '#FFD600', color: '#810081', fontWeight: '600' }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Clear Search
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
      </div>
    </>
  );
};

export default ArticlesPage;